// ============================================================
// deleteCaseError.ts
// ONE place that turns a failed case DELETE into user-facing copy, shared by
// the case list (single + bulk) and the shared CaseActionsMenu (sidebar etc.).
//
// A refusal (403 — e.g. "Only the case's assignee or an admin can delete this
// case.", or a 404 for a case that's already gone) is PERMANENT, so we show the
// server's reason and never suggest retrying. Anything else (network, 5xx)
// might be transient → keep the retry wording.
//
// Reads the server envelope straight off the response (not err.message), so it
// doesn't depend on the axios interceptor having rewritten the message.
// ============================================================
import axios from 'axios'

export interface DeleteFailure {
  // true → a refusal (don't offer retry); false → transient/unknown.
  refused: boolean
  message: string
}

export const describeDeleteCaseError = (err: unknown): DeleteFailure => {
  if (axios.isAxiosError<{ error?: unknown }>(err) && err.response) {
    const status = err.response.status
    const serverMsg = typeof err.response.data?.error === 'string' ? err.response.data.error : ''
    if (status === 403) {
      return { refused: true, message: serverMsg || "You don't have permission to delete this case." }
    }
    if (status === 404) {
      return { refused: true, message: serverMsg || 'This case no longer exists.' }
    }
    if (status === 409) {
      // e.g. CASE_HAS_TIME_ENTRIES — a permanent refusal with the server's reason.
      return { refused: true, message: serverMsg || "This case can't be deleted." }
    }
  }
  return { refused: false, message: 'Failed to delete case. Please try again.' }
}

// Bulk summary: "2 of 5 cases couldn't be deleted: <reason>". When refusals
// carry different reasons, the first refusal's reason is shown; a transient
// failure among them adds the retry hint.
export const summariseBulkDeleteFailures = (total: number, failures: DeleteFailure[]): string | null => {
  if (failures.length === 0) return null
  const refusal = failures.find((f) => f.refused)
  const anyTransient = failures.some((f) => !f.refused)
  const n = failures.length
  const head = `${n} of ${total} case${total === 1 ? '' : 's'} couldn't be deleted`
  if (refusal) {
    return `${head}: ${refusal.message}${anyTransient ? ' Some failed for another reason — please try those again.' : ''}`
  }
  return `${head}. Please try again.`
}
